import { useState } from 'react';

import { BioscopeContractList } from '@/components/molecules/BioscopeContractList/BioscopeContractList';
import type { NewContractFormValues } from '@/components/molecules/ModalCreateBioscopeContract/ModalCreateBioscopeContract';
import { BioscopeMovingModalContainer } from '@/components/organisms/MoveBioscopeModal/MoveBioscopeModal';
import {
    type CreateNewContractInput,
    type CreateNewContractLicenseInput,
    useAddContractLicense,
    useArchiveContract,
    useCreateContract,
    useDeleteContractLicense,
    useGetClientContracts,
    useGetLicenseCatalogue,
    useUnarchiveContract,
    useUpdateContract,
    useUpdateContractLicense,
} from '@/domain/licenses';

interface BioscopeLicenseContainerProps {
    clientId: number;
}

export function BioscopeLicenseContainer({ clientId }: BioscopeLicenseContainerProps) {
    const [error, setError] = useState<unknown>(null);

    const { data: licenseData, isLoading: isLicenseDataLoading } = useGetLicenseCatalogue();

    const {
        data: clientContractData,
        isLoading: isClientContractLoading,
        refetch: refetchList,
    } = useGetClientContracts(clientId, { onError: setError });

    // The writes the modals fire go through `mutate`, which reports a failure through onError
    // instead of rejecting: the modals move on, so a rejection would go unhandled.
    const { mutate: createLicense } = useAddContractLicense({
        onSuccess: () => refetchList(),
        onError: setError,
    });
    const { mutate: createContract } = useCreateContract({
        onSuccess: () => refetchList(),
        onError: setError,
    });

    const { mutate: updateContract } = useUpdateContract({
        onSuccess: () => refetchList(),
        onError: setError,
    });
    // Archiving and deleting run inside a confirmation, which waits for them and shows a
    // failure in the dialog, so these reject instead.
    const { mutateAsync: archiveContract } = useArchiveContract({
        onSuccess: () => refetchList(),
    });
    const { mutateAsync: unarchiveContract } = useUnarchiveContract({
        onSuccess: () => refetchList(),
    });
    const { mutate: updateLicense } = useUpdateContractLicense({
        onSuccess: () => refetchList(),
        onError: setError,
    });
    const { mutateAsync: deleteLicense } = useDeleteContractLicense({
        onSuccess: () => refetchList(),
    });

    const [movingBioscope, setMovingBioscope] = useState<number>();
    const [isMovingModalOpen, setIsMovingModalOpen] = useState(false);

    const input = {
        licenseList: licenseData ?? [],
        contractList: clientContractData ?? [],
        isLoading: isLicenseDataLoading || isClientContractLoading,
        error: error,
        openMoveModal: (bioscopeId: number) => {
            setMovingBioscope(bioscopeId);
            setIsMovingModalOpen(true);
        },
        onCreateLicense: (contractId: number, newLicense: CreateNewContractLicenseInput) => {
            createLicense({ id: contractId, license: newLicense });
        },
        onCreateContract: (newContract: NewContractFormValues) => {
            createContract({ ...newContract, clientId });
        },
        onUpdateLicense: (
            contractId: number,
            contractLicenseId: number,
            license: CreateNewContractLicenseInput
        ) => {
            updateLicense({ contractId, contractLicenseId, license });
        },
        onDeleteLicense: (contractId: number, contractLicenseId: number) =>
            deleteLicense({ contractId, contractLicenseId }),
        onUpdateContract: (contractId: number, contract: CreateNewContractInput) => {
            updateContract({ contractId, contract });
        },
        onArchiveContract: (contractId: number) => archiveContract(contractId),
        onUnarchiveContract: (contractId: number) => unarchiveContract(contractId),
    };

    const onCloseBioscopeModal = () => {
        setIsMovingModalOpen(false);
        refetchList();
    };

    const handleMoveSuccess = () => {
        refetchList();
        setIsMovingModalOpen(false);
    };

    return (
        <>
            {
                <BioscopeMovingModalContainer
                    bioscopeId={movingBioscope}
                    isOpen={isMovingModalOpen}
                    onClose={onCloseBioscopeModal}
                    onMoveSuccess={handleMoveSuccess}
                />
            }
            <BioscopeContractList {...input} />
        </>
    );
}
